import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { SCHEDULABLE_REPORTS } from '../consts/reportScheduling';
import {
  getReportEmailSettings,
  sendReportPreviewEmail,
  updateReportEmailSettings,
} from '../slices/reportEmailSettings';
import { getReportSendHistory } from '../slices/reportSendHistory';
import { getReportSheetSync } from '../slices/reportSheetSync';
import { updateCronInformation } from '../slices/cronSlice';
import { buildCronInterval } from '../helpers/cronExpression';

export const useReportScheduling = (reportId, previewParams = {}) => {
  const config = reportId ? SCHEDULABLE_REPORTS[reportId] : null;
  const isSchedulable = Boolean(config);
  const dispatch = useDispatch();
  const emailSettings = useSelector((state) => state.reportEmailSettings?.settings?.[reportId]);
  const isLoadingEmailSettings = useSelector((state) => state.reportEmailSettings?.loading);
  const sendHistory = useSelector((state) => state.reportSendHistory?.sendHistory?.[reportId] || []);
  const isLoadingSendHistory = useSelector((state) => state.reportSendHistory?.loading);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isSchedulable) return;
    dispatch(getReportEmailSettings(reportId));
    dispatch(getReportSendHistory(reportId));
    dispatch(getReportSheetSync(reportId));
  }, [dispatch, isSchedulable, reportId]);

  const handleSuccessSubmit = useCallback(async (formData) => {
    if (!config) return;
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth || null,
      slackChannelId: formData?.slackChannelId || null,
      dayOfWeek: formData?.dayOfWeek || null,
      timeOfDay: formData?.timeOfDay || null,
      emailMessage: formData?.emailMessage || null,
    };
    try {
      await dispatch(updateReportEmailSettings({ reportType: reportId, ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      await dispatch(updateCronInformation({
        singleData: {
          id: emailSettings?.cronId,
          type: emailSettings?.cronType || config.cronType,
          cronInterval,
        },
      })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpen(false);
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to save settings', { position: 'bottom-right' });
    }
  }, [config, dispatch, emailSettings, reportId]);

  const sendPreview = useCallback(async (formData) => {
    try {
      await dispatch(sendReportPreviewEmail({
        reportType: reportId,
        email: formData?.emailSendPreviewTo,
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        type: formData?.type || null,
        ...previewParams,
        ...(formData?.startDate ? { startDate: formData.startDate } : {}),
        ...(formData?.endDate ? { endDate: formData.endDate } : {}),
      })).unwrap();
      await dispatch(getReportSendHistory(reportId)).unwrap();
      toast.success(formData?.slackChannelId ? 'Slack preview sent' : 'Preview email sent', { position: 'bottom-right' });
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to send preview', { position: 'bottom-right' });
    }
  }, [dispatch, previewParams, reportId]);

  if (!isSchedulable) return { isSchedulable: false };
  return {
    isSchedulable: true,
    emailSettings,
    isLoadingEmailSettings,
    sendHistory,
    isLoadingSendHistory,
    handleSuccessSubmit,
    sendPreviewEmail: sendPreview,
    sendPreviewSlack: sendPreview,
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
  };
};

export default useReportScheduling;
